import { Routes } from '@angular/router';

import { authGuard } from './common/guards/auth.guard';

/**
 * Sections that are built but hidden from this app for now — only Market Research is
 * live. Their code is untouched; they're just not registered below, so nothing links or
 * navigates to them.
 *
 * To bring one back: move its entry into the shell's `children` (above the `**` catch-all)
 * and restore its sidenav link in MainLayoutComponent (see `HIDDEN_NAV`).
 *
 * Kept as real Route objects rather than commented out, so the build still type-checks
 * these lazy imports — a hidden page can't silently rot while it waits.
 */
export const HIDDEN_ROUTES: Routes = [
  {
    path: 'admin',
    loadComponent: () =>
      import('./super-god-bhive/admin-panel/admin-panel.component').then(
        (m) => m.AdminPanelComponent
      )
  },
  {
    path: 'users',
    loadComponent: () =>
      import('./super-god-bhive/manage-users/manage-users.component').then(
        (m) => m.ManageUsersComponent
      )
  },
  {
    path: 'notification-templates',
    loadComponent: () =>
      import(
        './super-god-bhive/master-notification/edit-noti-template/edit-noti-template.component'
      ).then((m) => m.EditNotiTemplateComponent)
  },
  {
    path: 'incheck-subscriptions',
    loadComponent: () =>
      import(
        './super-god-bhive/incheck-subscription-activation/incheck-subscription-activation.component'
      ).then((m) => m.IncheckSubscriptionActivationComponent)
  },
  {
    path: 'onboarding',
    loadComponent: () =>
      import(
        './super-god-bhive/new-user-onboarding/new-user-onboarding.component'
      ).then((m) => m.NewUserOnboardingComponent)
  },
  {
    path: 'dashboard',
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'overview' },
      {
        path: 'overview',
        loadComponent: () =>
          import('./super-god-bhive/internal-dashboard/overview/overview.component').then(
            (m) => m.OverviewComponent
          ),
      },
      {
        path: 'brands',
        loadComponent: () =>
          import('./super-god-bhive/internal-dashboard/brands/brands.component').then(
            (m) => m.BrandsComponent
          ),
      },
      {
        path: 'countries',
        loadComponent: () =>
          import('./super-god-bhive/internal-dashboard/countries/countries.component').then(
            (m) => m.CountriesComponent
          ),
      },
      {
        path: 'trials',
        loadComponent: () =>
          import('./super-god-bhive/internal-dashboard/trials/trials.component').then(
            (m) => m.TrialsComponent
          ),
      },
      {
        path: 'retention',
        loadComponent: () =>
          import('./super-god-bhive/internal-dashboard/retention/retention.component').then(
            (m) => m.RetentionComponent
          ),
      },
      {
        path: 'revenue',
        loadComponent: () =>
          import('./super-god-bhive/internal-dashboard/revenue/revenue.component').then(
            (m) => m.RevenueComponent
          ),
      },
      {
        path: 'chemicals',
        loadComponent: () =>
          import('./super-god-bhive/internal-dashboard/chemicals/chemicals.component').then(
            (m) => m.ChemicalsComponent
          ),
      },
      {
        path: 'market',
        loadComponent: () =>
          import(
            './super-god-bhive/internal-dashboard/market-research/market-research.component'
          ).then((m) => m.MarketResearchComponent),
      },
    ],
  }
];

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./login-page/login-page.component').then((m) => m.LoginPageComponent)
  },
  {
    path: '',
    canActivate: [authGuard],
    canActivateChild: [authGuard],
    loadComponent: () =>
      import('./layout/main-layout/main-layout.component').then((m) => m.MainLayoutComponent),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'market-research' },
      {
        path: 'internal-dashboard/brand-overview',
        loadComponent: () =>
          import(
            './super-god-bhive/internal-dashboard/brand-overview/container/brand-overview.component'
          ).then((m) => m.BrandOverviewComponent)
      },
      {
        path: 'market-research',
        loadComponent: () =>
          import(
            './super-god-bhive/market-research/market-research.component'
          ).then((m) => m.MarketResearchComponent),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'matching-flow' },
          {
            path: 'matching-flow',
            loadComponent: () =>
              import(
                './super-god-bhive/market-research/container/matching-flow-container.component'
              ).then((m) => m.MatchingFlowContainerComponent)
          },
          {
            path: 'smart-search',
            loadComponent: () =>
              import(
                './super-god-bhive/market-research/components/smart-search/smart-search.component'
              ).then((m) => m.SmartSearchComponent)
          }
        ]
      },
      // Hidden sections (/admin, /users, /dashboard/…) and any unknown URL land on the
      // matching flow instead of a blank shell.
      { path: '**', redirectTo: 'market-research' }
    ]
  }
];
